'use client';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ArrowLeft, Menu } from 'lucide-react';
import Link from 'next/link';

interface Event {
  year: number;
  colorScheme: {
    backgroundColor: string;
    textColor: string;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    backgroundSecondaryColor: string;
    textSecondaryColor: string;
    cardBackgroundColor: string;
  } | null;
}

interface BurgerMenuProps {
  latestEvent: Event;
  otherEvents: Event[];
  colorScheme: Event['colorScheme'];
  showCurrentEventButton?: boolean;
  currentYear?: number;
  allEvents?: Event[];
}

const BurgerMenu = ({
  latestEvent,
  otherEvents,
  colorScheme,
  showCurrentEventButton = false,
  currentYear,
  allEvents = []
}: BurgerMenuProps) => {
  const isHomepage = !showCurrentEventButton;

  return (
    <div
      className={
        isHomepage
          ? 'flex items-center justify-end w-full'
          : 'flex items-center justify-between w-full'
      }>
      {/* Left side - Current Event Button (only show if not on current event) */}
      {showCurrentEventButton && latestEvent && currentYear !== latestEvent.year && (
        <Button
          variant='ghost'
          size='sm'
          asChild
          className='hover:bg-primary/10 text-foreground border-0 focus:ring-2 focus:ring-primary/20 focus:ring-offset-0'
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            color: colorScheme?.textColor || '#1F2937'
          }}>
          <Link href='/' className='flex items-center gap-2'>
            <ArrowLeft className='w-4 h-4' />
            Current Event
          </Link>
        </Button>
      )}

      {/* Right side - Burger Menu */}
      <div className={isHomepage ? 'w-full flex justify-end' : 'ml-auto'}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='sm'
              className='hover:bg-primary/10 text-foreground border-0 focus:ring-2 focus:ring-primary/20 focus:ring-offset-0'
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: colorScheme?.textColor || '#1F2937'
              }}>
              <Menu className='w-5 h-5' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align='end'
            className='w-56 shadow-xl backdrop-blur-sm rounded-lg overflow-hidden'
            style={{
              backgroundColor: colorScheme?.cardBackgroundColor || '#FFFFFF',
              color: colorScheme?.textColor || '#1F2937',
              border: `1px solid ${colorScheme?.backgroundSecondaryColor || '#F8FAFC'}`,
              boxShadow:
                '0 10px 38px -10px rgba(0, 0, 0, 0.35), 0 10px 20px -15px rgba(0, 0, 0, 0.2)'
            }}>
            <DropdownMenuLabel
              className='font-medium px-3 py-2 text-sm border-b'
              style={{
                color: colorScheme?.textSecondaryColor || '#6B7280',
                borderBottomColor: colorScheme?.backgroundSecondaryColor || '#F8FAFC'
              }}>
              Events
            </DropdownMenuLabel>

            {isHomepage ? (
              <>
                {/* Homepage - Current event first */}
                <DropdownMenuItem asChild>
                  <Link
                    href='/'
                    className='flex items-center justify-between px-3 py-2.5 text-sm transition-colors duration-150 cursor-pointer'
                    style={{
                      color: colorScheme?.textColor || '#1F2937',
                      backgroundColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${colorScheme?.primaryColor || '#3B82F6'}10`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}>
                    <span className='font-medium'>{latestEvent.year}</span>
                    <span
                      className='text-xs px-2 py-0.5 rounded-full font-medium'
                      style={{
                        backgroundColor: colorScheme?.primaryColor || '#3B82F6',
                        color: colorScheme?.backgroundColor || '#FFFFFF'
                      }}>
                      Current
                    </span>
                  </Link>
                </DropdownMenuItem>

                {/* Other events */}
                {otherEvents.map((event) => (
                  <DropdownMenuItem key={event.year} asChild>
                    <Link
                      href={`/${event.year}`}
                      className='flex items-center justify-between px-3 py-2.5 text-sm transition-colors duration-150 cursor-pointer'
                      style={{
                        color: colorScheme?.textSecondaryColor || '#6B7280',
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = `${colorScheme?.backgroundSecondaryColor || '#F8FAFC'}80`;
                        e.currentTarget.style.color = colorScheme?.textColor || '#1F2937';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = colorScheme?.textSecondaryColor || '#6B7280';
                      }}>
                      <span>{event.year}</span>
                      <span className='text-xs opacity-60' style={{ color: 'inherit' }}>
                        Archive
                      </span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </>
            ) : (
              <>
                {/* [year] page - Latest event first */}
                {latestEvent && (
                  <DropdownMenuItem asChild>
                    <Link
                      href='/'
                      className='flex items-center justify-between px-3 py-2.5 text-sm transition-colors duration-150 cursor-pointer'
                      style={{
                        color:
                          currentYear === latestEvent.year
                            ? colorScheme?.textColor || '#1F2937'
                            : colorScheme?.textColor || '#1F2937',
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = `${colorScheme?.primaryColor || '#3B82F6'}10`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}>
                      <span
                        className={
                          currentYear === latestEvent.year ? 'font-medium' : 'font-medium'
                        }>
                        {latestEvent.year}
                      </span>
                      {currentYear !== latestEvent.year && (
                        <span
                          className='text-xs px-2 py-0.5 rounded-full font-medium'
                          style={{
                            backgroundColor: colorScheme?.primaryColor || '#3B82F6',
                            color: colorScheme?.backgroundColor || '#FFFFFF'
                          }}>
                          Current
                        </span>
                      )}
                      {currentYear === latestEvent.year && (
                        <span
                          className='text-xs px-2 py-0.5 rounded-full font-medium'
                          style={{
                            backgroundColor: colorScheme?.accentColor || '#F59E0B',
                            color: colorScheme?.backgroundColor || '#FFFFFF'
                          }}>
                          Viewing
                        </span>
                      )}
                    </Link>
                  </DropdownMenuItem>
                )}

                {/* Other events */}
                {allEvents.slice(1).map((yearEvent) => (
                  <DropdownMenuItem key={yearEvent.year} asChild>
                    <Link
                      href={`/${yearEvent.year}`}
                      className='flex items-center justify-between px-3 py-2.5 text-sm transition-colors duration-150 cursor-pointer'
                      style={{
                        color:
                          currentYear === yearEvent.year
                            ? colorScheme?.textColor || '#1F2937'
                            : colorScheme?.textSecondaryColor || '#6B7280',
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = `${colorScheme?.backgroundSecondaryColor || '#F8FAFC'}80`;
                        e.currentTarget.style.color = colorScheme?.textColor || '#1F2937';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color =
                          currentYear === yearEvent.year
                            ? colorScheme?.textColor || '#1F2937'
                            : colorScheme?.textSecondaryColor || '#6B7280';
                      }}>
                      <span className={currentYear === yearEvent.year ? 'font-medium' : ''}>
                        {yearEvent.year}
                      </span>
                      {currentYear === yearEvent.year ? (
                        <span
                          className='text-xs px-2 py-0.5 rounded-full font-medium'
                          style={{
                            backgroundColor: colorScheme?.accentColor || '#F59E0B',
                            color: colorScheme?.backgroundColor || '#FFFFFF'
                          }}>
                          Viewing
                        </span>
                      ) : (
                        <span className='text-xs opacity-60' style={{ color: 'inherit' }}>
                          Archive
                        </span>
                      )}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default BurgerMenu;
